import { useCallback, useMemo, useRef } from 'react';
import { http } from '@frontend/fetch';
import { useLocationDataShallowStore } from '@frontend/location-helpers';
import { LocationPickerProvider } from '@frontend/location-picker';
import { usePanelStore } from '@frontend/panel-engine';
import { useAppScopeCatalog, useAppScopeNaming, useAppScopeSelection, useAppScopeSetters } from '@frontend/scope';
import { useContactPanelStore } from '@frontend/shared';
import { useSlidePanelStore } from '@frontend/slide-panel';
import { useAlert } from '@frontend/design-system';
import { useConfigureLocation } from '../../../utils/configure-location';
import {
  UnprivilegedLocationProvider,
  UnprivilegedLocationPicker,
} from './unprivileged-multi-location-selector-content';
import { useUnprivilegedPhoneSoftphoneIntegration } from './use-unprivileged-phone-softphone-integration';

const UnprivilegedPickerProviders = ({
  children,
  closeModal,
}: {
  children: React.ReactNode;
  closeModal?: () => void;
}) => {
  const alerts = useAlert();
  const { setMultiSelectedIds } = useLocationDataShallowStore('setMultiSelectedIds');

  const { selectedLocationIds, selectedGroupId, forcedGroupId, selectedOrgId } = useAppScopeSelection();
  const { getScopeName } = useAppScopeNaming();
  const { accessibleScopeHierarchy } = useAppScopeCatalog();
  const { setSelectedLocationIds } = useAppScopeSetters();

  const { configureLocationData } = useConfigureLocation();

  const futureLocationIds = useRef<string[]>([]);
  const futureOrgId = useRef<string>('');

  const handleLocationChange = useCallback(
    ({ locationIds, orgId }: { locationIds: string[]; orgId: string }) => {
      const symDiff = locationIds
        .filter((x) => !selectedLocationIds.includes(x))
        .concat(selectedLocationIds.filter((x) => !locationIds.includes(x)));

      if (orgId === selectedOrgId && symDiff.length === 0) {
        console.log('No changes');
        futureLocationIds.current = [];
        futureOrgId.current = '';
        return;
      }

      // close all panels managed by the panel engine
      usePanelStore.getState().closeAllPanels();
      setMultiSelectedIds(locationIds);
      setSelectedLocationIds(locationIds, { skipValidation: true });
      useContactPanelStore.reset();
      useSlidePanelStore.reset();

      http.setLocationIdHeader(locationIds[0]);

      configureLocationData({
        // This is to satisfy the old single location paradigm
        newLocationId: locationIds[0],
        onError(errorMessage) {
          alerts.error({
            autoDismissAfter: 10000,
            message: errorMessage,
          });
        },
      });

      futureLocationIds.current = [];
      futureOrgId.current = '';
    },
    [alerts, configureLocationData, selectedLocationIds, selectedOrgId, setMultiSelectedIds, setSelectedLocationIds]
  );

  const {
    onClickApply,
    onApplyError,
    onClickOrgRadio,
    renderOrgInfo,
    renderLocationInfo,
    renderBannerInfo,
    renderPhoneRelatedModals,
  } = useUnprivilegedPhoneSoftphoneIntegration({
    handleLocationChange,
    closeModal,
    futureLocationIds,
    futureOrgId,
  });

  const organizationsIds = Object.keys(accessibleScopeHierarchy);
  const hasSingleOrganization = organizationsIds.length === 1;
  const hasSingleLocationGroup =
    hasSingleOrganization && Object.keys(accessibleScopeHierarchy[organizationsIds[0]]).length === 1;

  const inFlightOrgId = selectedOrgId || organizationsIds[0];

  const handleApply = useCallback(
    async (orgId: string, _selectedGroupId: string, locationIds: string[]) => {
      await onClickApply({ locationIds, orgId });
    },
    [onClickApply]
  );

  return (
    <>
      <LocationPickerProvider
        initialState={{
          organizationViewId: inFlightOrgId,
          inFlightOrganizationId: inFlightOrgId,
          inFlightLocationIds: selectedLocationIds,
          inFlightGroupId: selectedGroupId || forcedGroupId,
          groupViewId: selectedGroupId || forcedGroupId,
          hasSingleOrganization: hasSingleOrganization,
          hasSingleLocationGroup: hasSingleLocationGroup,
        }}
      >
        <UnprivilegedLocationProvider
          getScopeName={getScopeName}
          scopeHierarchy={accessibleScopeHierarchy}
          selectedGroupId={forcedGroupId}
          selectedLocationIds={selectedLocationIds}
          selectedOrgId={selectedOrgId}
          closeModal={closeModal}
          renderOrgInfo={renderOrgInfo}
          renderLocationInfo={renderLocationInfo}
          renderBannerInfo={renderBannerInfo}
          onApplyError={onApplyError}
          onApply={handleApply}
          onClickOrgRadio={onClickOrgRadio}
        >
          {children}
        </UnprivilegedLocationProvider>
      </LocationPickerProvider>
      {renderPhoneRelatedModals()}
    </>
  );
};

const WrappedPicker = ({ closeModal }: { closeModal?: () => void }) => {
  return (
    <UnprivilegedPickerProviders closeModal={closeModal}>
      <UnprivilegedLocationPicker />
    </UnprivilegedPickerProviders>
  );
};

/**
 * This picker has no side effects, and is to be used for the location picker page only
 */
const SimpleWrappedPicker = ({ closeModal }: { closeModal?: () => void }) => {
  const { setMultiSelectedIds } = useLocationDataShallowStore('setMultiSelectedIds');

  const { selectedLocationIds, selectedOrgId } = useAppScopeSelection();
  const { accessibleScopeHierarchy } = useAppScopeCatalog();
  const { getScopeName } = useAppScopeNaming();
  const { setSelectedLocationIds } = useAppScopeSetters();
  const { configureLocationData } = useConfigureLocation();
  const alerts = useAlert();

  const handleLocationChange = async ({ locationIds }: { locationIds: string[] }) => {
    setMultiSelectedIds(locationIds);
    setSelectedLocationIds(locationIds, { skipValidation: true });
    useContactPanelStore.reset();
    useSlidePanelStore.reset();
    http.setLocationIdHeader(locationIds[0]);
    configureLocationData({
      // This is to satisfy the old single location paradigm
      newLocationId: locationIds[0],
      onError(errorMessage) {
        alerts.error({
          autoDismissAfter: 10000,
          message: errorMessage,
        });
      },
    });
  };

  const onClickApply = async ({ locationIds }: { locationIds: string[] }) => {
    handleLocationChange({ locationIds });
  };

  const organizationsIds = useMemo(() => Object.keys(accessibleScopeHierarchy), [accessibleScopeHierarchy]);
  const hasSingleOrganization = organizationsIds.length === 1;

  const hasSingleLocationGroup = useMemo(
    () => hasSingleOrganization && Object.keys(accessibleScopeHierarchy[organizationsIds[0]]).length === 1,
    [accessibleScopeHierarchy, hasSingleOrganization, organizationsIds]
  );
  const inFlightOrgId = organizationsIds[0];

  const handleApply = useCallback(
    async (_orgId: string, _selectedGroupId: string, locationIds: string[]) => {
      await onClickApply({ locationIds });
    },
    [onClickApply]
  );

  // useAppScopeStore is taking a long time to load the data, so we need to wait for it to load before rendering the picker
  if (!inFlightOrgId) {
    return null;
  }

  return (
    <>
      <LocationPickerProvider
        initialState={{
          organizationViewId: inFlightOrgId,
          inFlightOrganizationId: inFlightOrgId,
          inFlightLocationIds: selectedLocationIds,
          inFlightGroupId: '',
          hasSingleOrganization,
          hasSingleLocationGroup,
        }}
      >
        <UnprivilegedLocationProvider
          getScopeName={getScopeName}
          scopeHierarchy={accessibleScopeHierarchy}
          selectedGroupId={''}
          selectedLocationIds={selectedLocationIds}
          selectedOrgId={selectedOrgId}
          closeModal={closeModal}
          isSimpleView={true}
          onApply={handleApply}
        >
          <UnprivilegedLocationPicker />
        </UnprivilegedLocationProvider>
      </LocationPickerProvider>
    </>
  );
};

export const UnprivilegedMultiLocationSelector = ({
  closeModal,
  simple = false,
}: {
  closeModal?: () => void;
  simple?: boolean;
}) => {
  return simple ? <SimpleWrappedPicker closeModal={closeModal} /> : <WrappedPicker closeModal={closeModal} />;
};
